import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAuthActions, useTenantsState } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight } from 'lucide-react';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export default function TenantSwitcher() {
  const { user } = useAuth();
  const { switchTenant } = useAuthActions();
  const { tenants, loading } = useTenantsState();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined);

  const options: ComboboxOption[] = useMemo(
    () => (tenants || []).map((t) => ({ value: t.tenantId, label: t.name })),
    [tenants]
  );

  if (loading || !tenants || tenants.length < 2) {
    return null;
  }

  const currentTenantId = user?.tenantId;
  const currentTenantName = options.find((o) => o.value === currentTenantId)?.label || 'Unknown';

  const handleOpenDialog = () => {
    setSelectedTenantId(currentTenantId);
    setDialogOpen(true);
  };

  const handleSwitch = () => {
    if (!selectedTenantId || selectedTenantId === currentTenantId) return;
    switchTenant({ tenantId: selectedTenantId });
    queryClient.removeQueries();
    navigate('/');
    setDialogOpen(false);
  };

  return (
    <>
      <div className="flex items-center justify-between rounded-md border px-3 py-2 text-xs">
        <span className="truncate font-medium">{currentTenantName}</span>
        <button
          onClick={handleOpenDialog}
          className="ml-2 shrink-0 rounded-sm p-1 opacity-70 hover:opacity-100 hover:bg-accent transition-colors"
          aria-label="Switch tenant"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch Account</DialogTitle>
            <DialogDescription>Select an account to switch to.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Combobox
              options={options}
              value={selectedTenantId}
              onValueChange={setSelectedTenantId}
              placeholder="Select account..."
              emptyText="No accounts found."
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSwitch}
              disabled={!selectedTenantId || selectedTenantId === currentTenantId}
            >
              Change Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
